import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';

import { Message } from '@qtek/shared/models';
import { AgencyActions } from './agency.actions';
import { ItemService } from './agency.service';

@Injectable()
export class AgencyEffects {
  constructor(
    private actions$: Actions,
    private itemService: ItemService
  ) {}

  connectWithCommand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgencyActions.subscribeItem),
      mergeMap(({ q, view, mysid, op, prms }) =>
        this.itemService.subscribe({ q, view, mysid, op, prms }).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(AgencyActions.unsubscribeItem),
              filter(action => mysid === action.mysid)
            )
          )
        )
      ),
      map(({ res, op, sts }: any) => {
        switch (op) {
          case 'query':
          case 'fast_query':
            return res
              ? AgencyActions.getItemsSuccess({
                  res: res || [],
                  meta: sts?.meta,
                })
              : AgencyActions.getItemsFailure({ payload: sts });
          case 'get':
            return res
              ? AgencyActions.getItemSuccess({ res })
              : AgencyActions.getItemFailure({ payload: sts });
          case 'ins':
            return res
              ? AgencyActions.createItemSuccess({
                  res,
                  message: new Message('GNR_ITM_CREATED'),
                })
              : AgencyActions.createItemFailure({ payload: sts });
          case 'upd':
            return res
              ? AgencyActions.updateItemSuccess({
                  res,
                  message: new Message('GNR_ITM_UPDATED'),
                })
              : AgencyActions.updateItemFailure({ payload: sts });
          case 'del':
            return res
              ? AgencyActions.deleteItemSuccess({
                  res,
                  message: new Message('GNR_ITM_DELETED'),
                })
              : AgencyActions.deleteItemFailure({ payload: sts });
          default:
            return { type: 'NOT_EXIST' };
        }
      })
    )
  );

  items$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AgencyActions.getItems),
        tap(({ payload }) => this.itemService.getItems(payload))
      ),
    { dispatch: false }
  );

  getItemById$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AgencyActions.getItem),
        tap(({ id }) => this.itemService.getItemById({ id }))
      ),
    { dispatch: false }
  );

  createItem$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AgencyActions.createItem),
        tap(({ payload }) => this.itemService.createItem({ res: payload }))
      ),
    { dispatch: false }
  );

  updateItem$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AgencyActions.updateItem),
        tap(({ payload }) =>
          this.itemService.updateItem({ res: payload, id: payload.seqno })
        )
      ),
    { dispatch: false }
  );

  deleteItem$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AgencyActions.deleteItem),
        tap(({ id }) => this.itemService.deleteItem({ id }))
      ),
    { dispatch: false }
  );
}
